// penPopup — Classic 常設フロート「ペン」(ペン種ピッカー) の位置 / 可視 / 永続化
// pure helper (node vitest 対象). LayerPopup の `layerPopup.ts` / 旧 `texturePopup.ts` と
// 同設計 (= position model + 可視 state + localStorage 記憶を UI 非依存に切り出し、
// PenPopup.tsx はこれを組み合わせるだけ)。
//
// 背景 (consult 2026-06-20, A 採択): Classic のペン種選択を **単一の常設フロート「ペン」**
// に集約する。これまで ToolPanel に並んでいたインライン ペン種 fieldset
// ([ブラシ][サインペン]) + [テクスチャ] 入口 + TexturePopup を 1 フロートに吸収し、
// ToolPanel には [ペン] トグル入口 1 個 ([プレビュ]/[レイヤー] 同型) だけを置く。
// フロートは [プレビュ]/[レイヤー] と同型の「常時参照型 floating panel」。
//
// 旧 `texturePopup.ts` (位置のみ) を本ファイルへ一般化 + 可視 state 永続化を追加。
// localStorage キーは旧 texture-popup から pen-popup へ移行 (= 別キー)。

import type { SidebarDockSide } from './classicToolFloat.js';
import type { FloatingPopupPosition, StorageLike } from './layerPopup.js';

/** デフォルト位置を出す際の画面端マージン (px、LayerPopup と揃える). */
export const PEN_POPUP_MARGIN = 16;

/**
 * popup のおおよその幅 / 高さ (= デフォルト位置 / 初回 clamp fallback 用. 実寸は CSS が
 * 決める). PenPopup.tsx の幅と揃える。高さは「ヘッダ + ペン種チップ + (texture のとき)
 * preset グリッド」の概算。
 */
export const PEN_POPUP_NOMINAL_WIDTH = 240;
export const PEN_POPUP_NOMINAL_HEIGHT = 140;

/**
 * popup のデフォルト位置 = 画面右上寄り (描画の邪魔になりにくい隅、LayerPopup と同流儀)。
 * LayerPopup / PreviewOverlay と重ならないよう少し下げて出す。
 *
 * `innerWidth` / `innerHeight` を引数で受ける (= node test で window 非依存に検証できる)。
 * SSR / 計測不能時は左上 fallback (margin, margin)。
 *
 * 2026-08-01 追加依頼: `dockSide` (既定 `'left'`) が `'right'` のときは
 * LayerPopup と同じ理由 (サイドバーの常設グリップ帯と重なる) で左上へミラーする。
 * y の下げ幅 (LayerPopup を避ける) はミラー先でも同じ理屈で必要なため据え置く。
 * 保存済み位置がある場合はこの関数を呼ばない (呼び出し側優先、LayerPopup と同型)。
 */
export const defaultPenPopupPosition = (
  innerWidth: number,
  innerHeight: number,
  dockSide: SidebarDockSide = 'left',
): FloatingPopupPosition => {
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight)) {
    return { x: PEN_POPUP_MARGIN, y: PEN_POPUP_MARGIN };
  }
  if (dockSide === 'right') {
    return { x: PEN_POPUP_MARGIN, y: PEN_POPUP_MARGIN + 220 };
  }
  return {
    x: Math.max(PEN_POPUP_MARGIN, innerWidth - PEN_POPUP_NOMINAL_WIDTH - PEN_POPUP_MARGIN),
    // LayerPopup (= y=margin) と被らないよう 1 段下げる (margin + おおよそ LayerPopup の
    // 高さ ≒ 220px (うすさスライダ行で 44px 高に))。画面が低い場合は初回 clamp が画面内へ引き戻す。
    y: PEN_POPUP_MARGIN + 220,
  };
};

/** localStorage キー: popup 位置 (per-user 記憶, nice-to-have). */
export const PEN_POPUP_POS_KEY = 'oekaki:classic-pen-popup-pos';

/**
 * 保存済 position を読み出す. 不在 / 不正 JSON / storage 不可は `null`
 * (= caller がデフォルト位置に倒す). 例外は throw しない.
 */
export const readPenPopupPosition = (storage: StorageLike): FloatingPopupPosition | null => {
  try {
    const raw = storage.getItem(PEN_POPUP_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  } catch {
    return null;
  }
};

/** position を保存. storage 不可 / quota 超過は silently drop. */
export const writePenPopupPosition = (storage: StorageLike, pos: FloatingPopupPosition): void => {
  try {
    storage.setItem(PEN_POPUP_POS_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
  } catch {
    // 永続化できないだけで動作は継続させる.
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 可視 (open/closed) state の永続化 (LayerPopup の visibility store と同設計)。
//
// - **default**: true (= 初見の Classic ユーザがペン種フロートの存在に必ず気づける。
//   集約でペン種ボタンが ToolPanel から消えるため、発見性を default open で担保する。
//   consult 2026-06-20 「初回は発見性のため open 既定を推奨」)。
// - **永続化**: 'true' / 'false' の string. 不正値・不在は default 扱い.
// - **storage 不可** (SSR / private browsing 等) は getters が default を返し、setters は
//   no-op (= 例外で UI が壊れない、layerPopup.ts と同流儀)。
// ─────────────────────────────────────────────────────────────────────────────

/** localStorage キー: PenPopup の可視状態 (per-user 記憶). */
export const PEN_POPUP_VISIBLE_KEY = 'oekaki:classic-pen-popup-visible';

/** PenPopup 可視状態のデフォルト値 (= 発見性のため open 既定、consult 2026-06-20). */
export const DEFAULT_PEN_POPUP_VISIBLE = true;

export interface PenPopupVisibilityStore {
  getInitial(): boolean;
  set(next: boolean): void;
}

export interface CreatePenPopupVisibilityStoreOptions {
  /**
   * 上書き用 storage. 既定は `globalThis.localStorage`. テストでは小さな Map ベースの
   * fake を渡す. `null` を渡すと no-op (= getters は常に default を返す).
   */
  readonly storage?: StorageLike | null;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:pen-popup-visible__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export const createPenPopupVisibilityStore = (
  opts: CreatePenPopupVisibilityStoreOptions = {},
): PenPopupVisibilityStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  return {
    getInitial(): boolean {
      if (storage === null) return DEFAULT_PEN_POPUP_VISIBLE;
      try {
        const raw = storage.getItem(PEN_POPUP_VISIBLE_KEY);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return DEFAULT_PEN_POPUP_VISIBLE;
      } catch {
        return DEFAULT_PEN_POPUP_VISIBLE;
      }
    },
    set(next: boolean): void {
      if (storage === null) return;
      try {
        storage.setItem(PEN_POPUP_VISIBLE_KEY, next ? 'true' : 'false');
      } catch {
        // 永続化できないだけで動作は継続させる.
      }
    },
  };
};
